/**
 * BookingDetailDrawer.tsx
 * Right-side detail panel for a MachineBooking (Machine Schedule UX — M3).
 *
 * Opens on a single click of a bar. Shows the booking's full detail and offers
 * inline edits that all flow through the optimistic commit funnel:
 *   - reschedule (start / end date pickers)
 *   - move to another capable machine (dropdown)
 *   - delete (guarded by the caller's ConfirmDialog)
 * Plus deep links to the source Quote / Work Order, assignees, and conflict state.
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  Drawer,
  Box,
  Typography,
  Chip,
  Divider,
  IconButton,
  Button,
  TextField,
  MenuItem,
  Stack,
  Avatar,
  Tooltip,
  Alert,
  Link,
} from '@mui/material';
import { Close, DeleteOutline, OpenInNew, DriveFileMoveOutlined } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import type { MachineBookingCalendar } from '../../types/schedule';
import type { MachineSummary, BookingPlacementPatch } from '../../hooks/useScheduleData';
import type { ConflictType } from './ConflictBadge';
import { fetchAssignmentsByTask } from '../../services/scheduleAssignments';
import type { TaskAssignee } from '../../services/scheduleTasks';
import { bookingPrimaryLabel, bookingTestName } from '../../utils/bookingLabel';
import {
  isContinuousRun,
  CONTINUOUS_BADGE,
  CONTINUOUS_HINT,
  CONTINUOUS_STAFFING_HINT,
} from '../../utils/runModeRendering';
import { useScheduleHolidays } from '../../hooks/useScheduleHolidays';
import {
  workingDaysCoveredInWeek,
  workingDaysInWeek,
} from './employeeScheduleShared';

interface BookingDetailDrawerProps {
  open: boolean;
  booking: MachineBookingCalendar | null;
  machines: MachineSummary[];
  /** Capable machineKeys for this booking, or null when any machine is allowed. */
  capableMachineKeys: Set<string> | null;
  conflictType: ConflictType | null;
  onCommitPlacement: (
    booking: MachineBookingCalendar,
    patch: BookingPlacementPatch,
    label: string,
  ) => void;
  onRequestDelete: (booking: MachineBookingCalendar) => void;
  /** When provided (hard conflict with a known fix), shows an auto-resolve action. */
  onResolveConflict?: () => void;
  /** Human label for the suggested resolution (shown on the resolve button). */
  resolveLabel?: string;
  onClose: () => void;
}

function inclusiveDays(start: string, end: string): number {
  const a = new Date(`${start}T00:00:00`).getTime();
  const b = new Date(`${end}T00:00:00`).getTime();
  return Math.round((b - a) / 86_400_000) + 1;
}

function bookingTitle(b: MachineBookingCalendar): string {
  // The drawer heading matches the bar: the work-order descriptive name (WO number
  // in brackets), the test name lives in its own field below.
  return bookingPrimaryLabel(b);
}

/** Two-letter initials from a display name ("Adin Richard" → "AR"). */
function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/);
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
  return (first + last).toUpperCase();
}

/** A Saturday week-start ISO → short label ("2026-06-22" → "Jun 22"). */
function formatWeekLabel(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

interface AssigneeWeek {
  weekStart: string;
  /** Widest [StartDate, EndDate] span across this user's rows for the week. */
  startDate: string | null;
  endDate: string | null;
}
interface AssigneeRow {
  userKey: string;
  name: string;
  tier: string | null;
  weeks: AssigneeWeek[];
}

/**
 * Collapse the flat per-(user, week) TaskAssignee rows into one row per employee
 * with their weekly day-span breakdown. Prefers rows whose Status matches the
 * booking (so a committed booking never shows a draft scenario's tentative
 * allocation), falling back to all rows when none match.
 *
 * P2-6 (audit #2): the stored EmployeeAssignment.Percent is IGNORED — staffing
 * is presence-based (Wave-3 Q4A), so the drawer speaks in busy DAYS, never in
 * the retired percent-allocation vocabulary.
 */
function groupAssigneesByUser(
  rows: TaskAssignee[] | null,
  bookingStatus: string | null,
): AssigneeRow[] {
  if (!rows || rows.length === 0) return [];
  const matched = bookingStatus ? rows.filter((r) => r.Status === bookingStatus) : rows;
  const use = matched.length > 0 ? matched : rows;
  const map = new Map<string, AssigneeRow>();
  for (const r of use) {
    const g =
      map.get(r.UserKey) ??
      { userKey: r.UserKey, name: r.displayName, tier: r.tier, weeks: [] };
    // One entry per week; a user staffing several machines in one week keeps
    // the WIDEST span (presence: busy on every covered day of any row).
    const existing = g.weeks.find((w) => w.weekStart === r.WeekStart);
    if (existing) {
      if (r.StartDate && (!existing.startDate || r.StartDate < existing.startDate)) existing.startDate = r.StartDate;
      if (r.EndDate && (!existing.endDate || r.EndDate > existing.endDate)) existing.endDate = r.EndDate;
    } else {
      g.weeks.push({ weekStart: r.WeekStart, startDate: r.StartDate ?? null, endDate: r.EndDate ?? null });
    }
    map.set(r.UserKey, g);
  }
  const out = [...map.values()];
  for (const g of out) g.weeks.sort((a, b) => a.weekStart.localeCompare(b.weekStart));
  return out.sort((a, b) => a.name.localeCompare(b.name));
}

/** ISO date `n` days after a week-start Saturday ('YYYY-MM-DD'). */
function weekDayAfter(weekStart: string, n: number): string {
  const [y, m, d] = weekStart.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10);
}

/**
 * Busy/working day counts for one assignee-week (holiday-aware). A row with no
 * explicit dates is a legacy full-week assignment → busy all working days.
 */
function weekBusyDayCounts(
  w: AssigneeWeek,
  holidays: ReadonlySet<string>,
): { busy: number; working: number } {
  const working = workingDaysInWeek(w.weekStart, holidays);
  const start = w.startDate ?? w.weekStart;
  const end = w.endDate ?? weekDayAfter(w.weekStart, 6);
  const busy = workingDaysCoveredInWeek(start, end, w.weekStart, holidays);
  return { busy, working };
}

const FieldLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography
    variant="caption"
    sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.4 }}
  >
    {children}
  </Typography>
);

export const BookingDetailDrawer: React.FC<BookingDetailDrawerProps> = ({
  open,
  booking,
  machines,
  capableMachineKeys,
  conflictType,
  onCommitPlacement,
  onRequestDelete,
  onResolveConflict,
  resolveLabel,
  onClose,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();

  // Holiday feed for the busy-day counts below (holiday-aware, same as every
  // other staffing surface). Best-effort: on failure counts degrade to Mon–Fri.
  const { holidays } = useScheduleHolidays(booking?.StartDate ?? '', booking?.EndDate ?? '');
  const holidaySet = useMemo(
    () => new Set(holidays.map((h) => h.date.slice(0, 10))),
    [holidays],
  );

  // Per-employee weekly busy-day breakdown for the ASSIGNED section. Fetched on
  // open from the existing task-assignees endpoint (per-(user, week) span rows —
  // the stored Percent is retired and never rendered, P2-6).
  // While loading / on error / for manual events, the flat Assignees chips show.
  const taskKey = booking?.ScheduleTaskKey ?? null;
  const [assignees, setAssignees] = useState<TaskAssignee[] | null>(null);
  useEffect(() => {
    if (!open || !taskKey) {
      setAssignees(null);
      return;
    }
    let cancelled = false;
    fetchAssignmentsByTask(taskKey)
      .then((rows) => {
        if (!cancelled) setAssignees(rows);
      })
      .catch(() => {
        if (!cancelled) setAssignees(null);
      });
    return () => {
      cancelled = true;
    };
  }, [open, taskKey]);

  const assigneeRows = useMemo(
    () => groupAssigneesByUser(assignees, booking?.Status ?? null),
    [assignees, booking?.Status],
  );

  if (!booking) {
    return <Drawer anchor="right" open={open} onClose={onClose} />;
  }

  const name = bookingTitle(booking);
  const duration = inclusiveDays(booking.StartDate, booking.EndDate);
  // RM-4: a continuous (24/7) booking's day count is CALENDAR days and its
  // staffing covers every WORKING day of the run (lab answer Q2, 2026-07-10)
  // — the drawer says both.
  const continuous = isContinuousRun(booking.RunMode);

  // Machines this booking may move to (capable + active), current one first.
  const targetMachines = machines.filter(
    (m) => capableMachineKeys === null || capableMachineKeys.has(m.machineKey),
  );

  const commitDate = (field: 'StartDate' | 'EndDate', value: string) => {
    if (!value) return;
    const start = field === 'StartDate' ? value : booking.StartDate;
    const end = field === 'EndDate' ? value : booking.EndDate;
    if (end < start) return; // ignore invalid range; backend would reject anyway
    onCommitPlacement(booking, { [field]: value }, `Rescheduled ${name}`);
  };

  const commitMachine = (machineKey: string) => {
    if (machineKey === booking.MachineKey) return;
    const code = machines.find((m) => m.machineKey === machineKey)?.code ?? machineKey;
    onCommitPlacement(booking, { MachineKey: machineKey }, `Moved ${name} → ${code}`);
  };

  const statusColor = booking.Status === 'Committed' ? 'primary' : 'default';

  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: 360, maxWidth: '92vw' } }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, p: 2, pb: 1 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.25 }}>
              {name}
            </Typography>
            <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }}>
              <Chip size="small" label={booking.Status} color={statusColor} sx={{ height: 20, fontSize: '0.65rem' }} />
              <Chip
                size="small"
                variant="outlined"
                label={booking.EventType ?? booking.Source}
                sx={{ height: 20, fontSize: '0.65rem' }}
              />
              {/* RM-4: the 24/7 badge the clicked bar carries — the drawer used
                  to drop the continuous story entirely. */}
              {continuous && (
                <Tooltip title={CONTINUOUS_HINT} arrow>
                  <Chip
                    size="small"
                    label={CONTINUOUS_BADGE}
                    sx={{
                      height: 20,
                      fontSize: '0.65rem',
                      fontWeight: 700,
                      bgcolor: theme.schedule.continuous,
                      color: theme.palette.common.white,
                    }}
                  />
                </Tooltip>
              )}
            </Stack>
          </Box>
          <IconButton size="small" onClick={onClose} aria-label="Close details">
            <Close fontSize="small" />
          </IconButton>
        </Box>

        <Divider />

        <Box sx={{ flex: 1, overflowY: 'auto', p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {conflictType && (
            <Alert
              severity={conflictType === 'hard' ? 'error' : 'warning'}
              sx={{ py: 0.5 }}
              action={
                onResolveConflict && conflictType === 'hard' ? (
                  <Button
                    color="inherit"
                    size="small"
                    onClick={onResolveConflict}
                    sx={{ textTransform: 'none', fontWeight: 700 }}
                  >
                    Auto-resolve
                  </Button>
                ) : undefined
              }
            >
              {conflictType === 'hard'
                ? `Hard conflict: overlaps another booking on this machine.${
                    resolveLabel ? ` Suggestion: ${resolveLabel}.` : ''
                  }`
                : 'Soft conflict: this machine may not be capable of the task.'}
            </Alert>
          )}

          {/* Work / Test / Project / Client — the "what & who" the bar no longer shows. */}
          {(bookingTestName(booking) || booking.ProjectName || booking.ClientName) && (
            <Box>
              <FieldLabel>Details</FieldLabel>
              <Stack spacing={0.75} sx={{ mt: 0.5 }}>
                {bookingTestName(booking) && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">Test</Typography>
                    <Typography variant="body2" fontWeight={600}>{bookingTestName(booking)}</Typography>
                  </Box>
                )}
                {booking.ProjectName && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">Project</Typography>
                    <Typography variant="body2">
                      {booking.ProjectName}
                      {booking.ProjectId ? ` (${booking.ProjectId})` : ''}
                    </Typography>
                  </Box>
                )}
                {booking.ClientName && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">Client</Typography>
                    <Typography variant="body2">{booking.ClientName}</Typography>
                  </Box>
                )}
              </Stack>
            </Box>
          )}

          {/* Machine + move */}
          <Box>
            <FieldLabel>Machine</FieldLabel>
            <TextField
              select
              size="small"
              fullWidth
              value={booking.MachineKey}
              onChange={(e) => commitMachine(e.target.value)}
              sx={{ mt: 0.5 }}
              InputProps={{
                startAdornment: <DriveFileMoveOutlined fontSize="small" sx={{ mr: 1, color: 'text.disabled' }} />,
              }}
            >
              {targetMachines.map((m) => (
                <MenuItem key={m.machineKey} value={m.machineKey}>
                  {m.code} — {m.name}
                </MenuItem>
              ))}
              {/* Keep the current machine selectable even if not in the capable set. */}
              {!targetMachines.some((m) => m.machineKey === booking.MachineKey) && (
                <MenuItem value={booking.MachineKey}>
                  {booking.MachineCode} — {booking.MachineName}
                </MenuItem>
              )}
            </TextField>
            {capableMachineKeys !== null && (
              <Typography variant="caption" color="text.secondary">
                Only machines capable of this task are listed.
              </Typography>
            )}
          </Box>

          {/* Dates */}
          <Box>
            <FieldLabel>Schedule</FieldLabel>
            <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
              <TextField
                type="date"
                size="small"
                label="Start"
                InputLabelProps={{ shrink: true }}
                value={booking.StartDate}
                onChange={(e) => commitDate('StartDate', e.target.value)}
                fullWidth
              />
              <TextField
                type="date"
                size="small"
                label="End"
                InputLabelProps={{ shrink: true }}
                value={booking.EndDate}
                onChange={(e) => commitDate('EndDate', e.target.value)}
                fullWidth
              />
            </Stack>
            <Typography variant="caption" color="text.secondary">
              {continuous
                ? `${duration} calendar day${duration === 1 ? '' : 's'} (24/7 — runs through weekends)`
                : `${duration} day${duration === 1 ? '' : 's'}`}
            </Typography>
          </Box>

          {/* References / deep links */}
          {(booking.QuoteKey || booking.TaskWorkOrderKey) && (
            <Box>
              <FieldLabel>Source</FieldLabel>
              <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                {booking.QuoteKey && (
                  <Link
                    component="button"
                    underline="hover"
                    sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontSize: '0.85rem' }}
                    onClick={() => navigate(`/quotes/${booking.QuoteKey}`)}
                  >
                    Quote {booking.QuoteNumber ?? booking.QuoteKey.slice(0, 8)} <OpenInNew sx={{ fontSize: 14 }} />
                  </Link>
                )}
                {booking.TaskWorkOrderKey && (
                  <Link
                    component="button"
                    underline="hover"
                    sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontSize: '0.85rem' }}
                    onClick={() =>
                      navigate(
                        // The WO detail route is /projects/:ProjectId/workorders/:WorkOrderKey.
                        // Without project context there is no single-WO route, so fall back to
                        // the Work Orders list rather than a dead /workorders/<guid> URL.
                        booking.ProjectId
                          ? `/projects/${booking.ProjectId}/workorders/${booking.TaskWorkOrderKey}`
                          : '/workorders',
                      )
                    }
                  >
                    Work Order {booking.WorkOrderNo ?? booking.TaskWorkOrderKey.slice(0, 8)}{' '}
                    <OpenInNew sx={{ fontSize: 14 }} />
                  </Link>
                )}
              </Stack>
            </Box>
          )}

          {/* Assignees — per-employee weekly busy-day breakdown. P2-6 (audit #2):
              staffing is presence-based (Wave-3), so this speaks in busy DAYS —
              the retired stored-percent bars ("avg 20%", error-red >100%) are gone. */}
          {(assigneeRows.length > 0 || booking.Assignees.length > 0) && (
            <Box>
              <FieldLabel>Assigned</FieldLabel>
              {/* RM-4 + lab answer Q2 (2026-07-10): staffing covers every working
                  day of the run — the hint explains why weekends stay empty
                  while the machine keeps running. */}
              {continuous && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', mt: 0.25 }}
                >
                  {CONTINUOUS_STAFFING_HINT}.
                </Typography>
              )}
              {assigneeRows.length > 0 ? (
                <Stack spacing={1.25} sx={{ mt: 0.75 }}>
                  {assigneeRows.map((u) => (
                    <Box key={u.userKey} sx={{ display: 'flex', gap: 1 }}>
                      <Avatar
                        sx={{
                          width: 30,
                          height: 30,
                          fontSize: '0.72rem',
                          bgcolor: theme.schedule.employeeAlloc,
                        }}
                      >
                        {initialsOf(u.name)}
                      </Avatar>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
                          <Typography variant="body2" fontWeight={600} noWrap sx={{ flex: 1, minWidth: 0 }}>
                            {u.name}
                          </Typography>
                          {u.tier && (
                            <Chip
                              size="small"
                              variant="outlined"
                              label={u.tier}
                              sx={{ height: 16, fontSize: '0.6rem' }}
                            />
                          )}
                          <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                            {u.weeks.length} wk{u.weeks.length !== 1 ? 's' : ''}
                          </Typography>
                        </Stack>
                        {/* One "busy X of Y days" row per BST week the task spans */}
                        <Stack spacing={0.25} sx={{ mt: 0.5 }}>
                          {u.weeks.map((w) => {
                            const { busy, working } = weekBusyDayCounts(w, holidaySet);
                            const label = `busy ${busy} of ${working} day${working !== 1 ? 's' : ''}`;
                            return (
                              <Box key={w.weekStart} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ width: 48, flexShrink: 0 }}
                                >
                                  {formatWeekLabel(w.weekStart)}
                                </Typography>
                                <Tooltip
                                  title={`${label} in the week of ${formatWeekLabel(w.weekStart)}${
                                    w.startDate && w.endDate ? ` (${w.startDate} – ${w.endDate})` : ''
                                  }`}
                                >
                                  <Typography variant="caption" fontWeight={600} sx={{ flex: 1, minWidth: 0 }} noWrap>
                                    {label}
                                  </Typography>
                                </Tooltip>
                              </Box>
                            );
                          })}
                        </Stack>
                      </Box>
                    </Box>
                  ))}
                </Stack>
              ) : (
                // Fallback: flat chips (detail still loading or unavailable).
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                  {booking.Assignees.map((a) => (
                    <Tooltip key={a.UserKey} title={a.Name}>
                      <Chip
                        size="small"
                        avatar={<Avatar sx={{ bgcolor: theme.schedule.employeeAlloc }}>{a.Initials}</Avatar>}
                        label={a.Name}
                        sx={{ height: 24 }}
                      />
                    </Tooltip>
                  ))}
                </Stack>
              )}
            </Box>
          )}

          {/* Notes */}
          {booking.Notes && (
            <Box>
              <FieldLabel>Notes</FieldLabel>
              <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
                {booking.Notes}
              </Typography>
            </Box>
          )}
        </Box>

        <Divider />

        {/* Footer actions */}
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between' }}>
          <Button
            color="error"
            startIcon={<DeleteOutline />}
            onClick={() => onRequestDelete(booking)}
            sx={{ textTransform: 'none' }}
          >
            Delete
          </Button>
          <Button onClick={onClose} sx={{ textTransform: 'none' }}>
            Close
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};
